import { Size } from './Size';

const ONE_SECOND_IN_MS = 1000;
const ONE_MINUTE_IN_SECONDS = 60;
const Time = {
  ONE_SECOND_IN_MS,
  ONE_QUARTER_SECOND_IN_MS: ONE_SECOND_IN_MS / 4,
  TWO_SECONDS_IN_MS: 2 * ONE_SECOND_IN_MS,
  THREE_SECONDS_IN_MS: 3 * ONE_SECOND_IN_MS,
  FIVE_SECONDS_IN_MS: 5 * ONE_SECOND_IN_MS,
  TEN_SECONDS_IN_MS: 10 * ONE_SECOND_IN_MS,
  FIFTEEN_SECONDS_IN_MS: 15 * ONE_SECOND_IN_MS,
  THIRTY_SECONDS_IN_MS: 30 * ONE_SECOND_IN_MS,
  ONE_MINUTE_IN_MS: ONE_MINUTE_IN_SECONDS * ONE_SECOND_IN_MS,
  ONE_MINUTE_IN_SECONDS,
  THREE_MINUTES_IN_SECONDS: 3 * ONE_MINUTE_IN_SECONDS,
} as const;

const ContainerFormats = {
  MKV: 'mkv',
  MP4: 'mp4',
  WEBM: 'webm',
} as const;

const VideoMimeTypes = {
  MKV: 'video/x-matroska',
  MP4: 'video/mp4',
  WEBM: 'video/webm',
} as const;

const VideoCodecs = {
  H264: 'h264',
  VP8: 'vp8',
  VP9: 'vp9',
  // For whatever reason, Safari uses a different codec name for H264
  SAFARI_H264: 'avc1',
  UNKNOWN: 'unknown',
} as const;

const AudioCodecs = {
  AAC: 'aac',
  OPUS: 'opus',
  // For whatever reason, Safari uses a different codec name for AAC
  SAFARI_AAC: 'mp4a',
  UNKNOWN: 'unknown',
} as const;

const DurableRecording = {
  PRE_SIGNED_URL_BATCH_SIZE: 50,
  CONTAINER_FORMATS_SUPPORTED_FOR_UPLOAD: [
    '3gp',
    'avi',
    'flv',
    'm4v',
    'mkv',
    'mov',
    'mp4',
    'mpeg',
    'mpg',
    'ts',
    'webm',
    'wmv',
  ],
  FILE_TYPES_SUPPORTED_FOR_UPLOAD: [
    '.3gp',
    '.avi',
    '.flv',
    '.m4v',
    '.mkv',
    '.mov',
    '.mp4',
    '.mpeg',
    '.mpg',
    '.ts',
    '.webm',
    '.wmv',
  ],
} as const;

const EventNames = {
  RemainingRecordedVideoData: {
    READY_TO_UPLOAD: 'remainingRecordedVideoData:readyToUpload',
  },
  RecordedVideoUploader: {
    CONNECTED: 'recordedVideoUploader:connected',
    PROGRESS_UPDATE: 'recordedVideoUploader:progressUpdate',
    UPLOAD_SPEED_UPDATE: 'recordedVideoUploader:uploadSpeedUpdate',
    UPLOAD_SUCCESS: 'recordedVideoUploader:uploadSuccess',
    ERROR: 'recordedVideoUploader:error',
  },
  RecordingStrategy: {
    CONNECTED: 'recordingStrategy:connected',
    PROGRESS_UPDATE: 'recordingStrategy:progressUpdate',
    UPLOAD_SPEED_UPDATE: 'recordingStrategy:uploadSpeedUpdate',
    UPLOAD_SUCCESS: 'recordingStrategy:uploadSuccess',
    ERROR: 'recordingStrategy:error',
    CONNECTING_TIME_REPORT: 'recordingStrategy:connectingTimeReport',
    STOPPING_TIME_REPORT: 'recordingStrategy:stoppingTimeReport',
  },
  SocketIO: {
    CONNECT: 'connect',
    CONNECT_ERROR: 'connect_error',
    DISCONNECT: 'disconnect',
  },
  EngineIO: {
    UPGRADE: 'upgrade',
  },
  EagerVideoCompletionManager: {
    ToServer: {
      START: 'start',
      RAW_CHUNK_READY_TO_EAGERLY_COMPLETE: 'rawChunkReadyToEagerlyComplete',
      FINISH: 'finish',
    },
    ToClient: {
      CONNECTED: 'connected',
      ERROR: 'error',
    },
  },
} as const;

const RecordingStrategyNames = {
  HTTP: 'http',
} as const;

const StandardAspectRatio = {
  aspectRatio: 4 / 3,
  aspectRatioLabel: '4x3',
  invertedAspectRatio: 3 / 4,
} as const;

const WidescreenAspectRatio = {
  aspectRatio: 16 / 9,
  aspectRatioLabel: '16x9',
  invertedAspectRatio: 9 / 16,
} as const;

const Resolutions = {
  SD_576: {
    label: 'SD_576',
    width: 768,
    height: 576,
    portraitWidth: 576,
    portraitHeight: 768,
    ...StandardAspectRatio,
  },
  HD_720P: {
    label: '720P',
    width: 1280,
    height: 720,
    portraitWidth: 720,
    portraitHeight: 1280,
    ...WidescreenAspectRatio,
  },
  HD_1080P: {
    label: '1080P',
    width: 1920,
    height: 1080,
    portraitWidth: 1080,
    portraitHeight: 1920,
    ...WidescreenAspectRatio,
  },
  UHD_4K: {
    label: '4K',
    width: 3840,
    height: 2160,
    portraitWidth: 2160,
    portraitHeight: 3840,
    ...WidescreenAspectRatio,
  },
} as const;

const BitRates = {
  AUDIO: Size.ONE_TWENTY_EIGHT_KBPS,
  VIDEO: Size.FIVE_MBPS,
  VIDEO_1080P: Size.EIGHT_MBPS,
  VIDEO_4K: Size.FORTY_FIVE_MBPS,
} as const;

type Dimensions = { width: number; height: number };
const DefaultDimensions: Dimensions = { width: 0, height: 0 } as const;

export {
  Time,
  ContainerFormats,
  VideoMimeTypes,
  DurableRecording,
  VideoCodecs,
  AudioCodecs,
  EventNames,
  RecordingStrategyNames,
  StandardAspectRatio,
  WidescreenAspectRatio,
  Resolutions,
  BitRates,
  DefaultDimensions,
  type Dimensions,
};
